import axios from "axios";
import endpoints from "./endpoints";

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL, // Replace with your API base URL
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// Add a request interceptor to include the token in headers
axiosInstance.interceptors.request.use((config) => {
  // Allow overriding Content-Type dynamically
  config.headers["Content-Type"] =
    config.headers["Content-Type"] ?? "application/json";

  // Do not attach token for login, forgot-password, and refresh-token endpoints
  if (config.url) {
    const urlWithoutParams = config.url.split('?')[0];
    if (
      urlWithoutParams.endsWith("/login") || 
      urlWithoutParams.endsWith("/forgot-password") ||
      urlWithoutParams.endsWith("/refresh-token") ||
      urlWithoutParams.includes("/auth/login") ||
      urlWithoutParams.includes("/auth/forgot-password") ||
      urlWithoutParams.includes("/auth/refresh-token")
    ) {
      return config;
    }
  }

  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("token="))
    ?.split("=")[1]; // Retrieve token from cookies
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add a response interceptor to handle errors globally
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Check if error is 401 and message indicates token expiration
    if (
      error.response?.status === 401 &&
      error.response?.data?.message === "Invalid or malformed token" &&
      !originalRequest._retry
    ) {
      if (isRefreshing) {
        // If token is being refreshed, queue the request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = document.cookie
        .split("; ")
        .find((row) => row.startsWith("refreshToken="))
        ?.split("=")[1];

      if (!refreshToken) {
        // No refresh token, logout
        isRefreshing = false;
        document.cookie = "token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC";
        document.cookie = "refreshToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC";
        window.location.href = "/login";
        return Promise.reject(error);
      }

      try {
        const response = await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}${endpoints.auth.refreshToken}`,
          { refreshToken },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data?.result?.token && response.data?.result?.refreshToken) {
          const { token, refreshToken: newRefreshToken } = response.data.result;
          
          // Save new tokens
          document.cookie = `token=${token}; path=/`;
          document.cookie = `refreshToken=${newRefreshToken}; path=/`;

          // Update authorization header
          axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
          originalRequest.headers.Authorization = `Bearer ${token}`;

          processQueue(null, token);
          isRefreshing = false;

          return axiosInstance(originalRequest);
        }
      } catch (refreshError: any) {
        processQueue(refreshError, null);
        isRefreshing = false;

        // Refresh token failed or expired, logout
        document.cookie = "token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC";
        document.cookie = "refreshToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC";
        
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
        
        return Promise.reject(refreshError);
      }
    }

    // Handle other 401 errors
    if (error.response?.status === 401) {
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }

    // Ensure the rejection reason is always an Error
    if (error instanceof Error) {
      return Promise.reject(error);
    } else {
      return Promise.reject(new Error(error?.message ?? "Unknown error"));
    }
  }
);

export default axiosInstance;
